import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PlatformUserSummary } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';

export function PlatformUsersPage() {
  const [rows, setRows] = useState<PlatformUserSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .platformListUsers({ page, pageSize: 10, search: search || undefined })
      .then((res) => {
        setRows(res.data);
        setTotalPages(res.totalPages);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [page, search]);

  return (
    <div className="page">
      <PageHeader title="Users" subtitle="Accounts across the platform" />
      <form
        className="platform-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <label className="field field--inline">
          <span className="field__label">Search</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or email"
          />
        </label>
        <button type="submit" className="btn btn--secondary">Search</button>
      </form>

      {loading ? <LoadingState message="Loading users…" /> : null}
      {error ? <ErrorBanner message={error} onRetry={load} /> : null}

      {!loading && !error && (
        <>
          {rows.length === 0 ? (
            <p className="empty-state">No users match your search.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id}>
                      <td>{u.fullName}</td>
                      <td>{u.email}</td>
                      <td><span className={`status-pill status-pill--${u.status}`}>{u.status}</span></td>
                      <td className="actions-cell">
                        <Link
                          to={`/platform/users/${u.id}`}
                          className="btn btn--ghost btn--sm"
                          aria-label={`View ${u.fullName}`}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="pagination">
              <button type="button" className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
